import { useState, useEffect, useCallback, useRef } from "react";
import Select, {
  MultiValue,
  InputActionMeta,
  components,
  OptionProps,
  MultiValueProps,
  MenuProps,
} from "react-select";
import { PatientRelationAxiosInstance } from "../../Network/PatientRelationAxiosInstance";
import { Option } from "../../interface/Option";

interface VipPatientSelectorProps {
  onChange: (selected: Option[]) => void;
  clear?: boolean;
  defaultValue?: Option[];
  value?: Option[];
  qaIdPrefix?: string;
}

const DEBOUNCE_MS = 350;
const MIN_SEARCH_LENGTH = 3;
const DEFAULT_QA_PREFIX = "vip-patient-selector";

const qaSafe = (value: string | number | undefined | null) =>
  String(value ?? "unknown")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const buildQaId = (prefix: string, suffix: string | number) =>
  `${qaSafe(prefix)}-${qaSafe(suffix)}`;

const qaAttrs = (prefix: string, suffix: string | number) => {
  const id = buildQaId(prefix, suffix);
  return {
    id,
    "data-testid": id,
    "data-qa": id,
  };
};

const VipPatientSelector = ({
  onChange,
  clear,
  defaultValue,
  value,
  qaIdPrefix = DEFAULT_QA_PREFIX,
}: VipPatientSelectorProps) => {
  const [options, setOptions] = useState<Option[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const selectQaId = buildQaId(qaIdPrefix, "select");
  const inputQaId = buildQaId(qaIdPrefix, "input");

  // -----------------------------------------------------------------
  // 1. Load ALL patients on first render (no search)
  // -----------------------------------------------------------------
  const loadAllPatients = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await PatientRelationAxiosInstance.get("searchpatient", {
        params: { Full_name: "", active: true },
      });

      const data = resp.data?.patients || [];
      const formatted = formatPatients(data);
      setOptions(formatted);
    } catch (err) {
      console.error("loadAllPatients error:", err);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // -----------------------------------------------------------------
  // 2. Load filtered patients (debounced, ≥ 3 chars)
  // -----------------------------------------------------------------
  const loadFilteredPatients = useCallback(async (search: string) => {
    if (search.length < MIN_SEARCH_LENGTH) {
      setOptions([]);
      return;
    }

    setLoading(true);
    try {
      const resp = await PatientRelationAxiosInstance.get("searchpatient", {
        params: { Full_name: search },
      });

      const data = resp.data?.patients || [];
      const formatted = formatPatients(data);
      setOptions(formatted);
    } catch (err) {
      console.error("loadFilteredPatients error:", err);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // -----------------------------------------------------------------
  // 3. Helper: format raw patient → Option + deduplication
  // ✅ Existing behavior preserved exactly as provided
  // -----------------------------------------------------------------
  const formatPatients = (data: any[]): Option[] => {
    const map = new Map<string | number, Option>();

    data.forEach((p) => {
      const key = p.id;
      const value = p.full_name;
      if (!map.has(key)) {
        map.set(key, {
          value: value,
          label: p.full_name || `Patient ${p.id}`,
        });
      }
    });

    return Array.from(map.values());
  };

  // -----------------------------------------------------------------
  // 4. Load all on mount
  // -----------------------------------------------------------------
  useEffect(() => {
    loadAllPatients();
  }, [loadAllPatients]);

  // -----------------------------------------------------------------
  // 5. Debounce input → search only if ≥ 3 chars
  // -----------------------------------------------------------------
  const onInputChange = (newValue: string, actionMeta: InputActionMeta) => {
    if (actionMeta.action !== "input-change") return;
    setInputValue(newValue);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!newValue.trim()) {
      debounceRef.current = setTimeout(() => loadAllPatients(), DEBOUNCE_MS);
      return;
    }

    if (newValue.length < MIN_SEARCH_LENGTH) {
      setOptions([]);
      return;
    }

    debounceRef.current = setTimeout(() => loadFilteredPatients(newValue), DEBOUNCE_MS);
  };

  // -----------------------------------------------------------------
  // 6. Selection handler
  // -----------------------------------------------------------------
  const handleChange = (selected: MultiValue<Option>) => {
    const arr = selected as Option[];
    setSelectedOptions(arr);
    onChange(arr);
  };

  // -----------------------------------------------------------------
  // 7. Clear / external value sync
  // -----------------------------------------------------------------
  useEffect(() => {
    if (clear) setSelectedOptions([]);
  }, [clear]);

  useEffect(() => {
    if (value) setSelectedOptions(value);
  }, [value]);

  useEffect(() => {
    if (!value && defaultValue) setSelectedOptions(defaultValue);
  }, [defaultValue, value]);

  // -----------------------------------------------------------------
  // 8. Custom message when < 3 chars
  // -----------------------------------------------------------------
  const noOptionsMessage = () => {
    if (!inputValue) return "Start typing or select from list...";
    if (inputValue.length < MIN_SEARCH_LENGTH)
      return `Type at least ${MIN_SEARCH_LENGTH} letters to search...`;
    return "No patients found";
  };

  // -----------------------------------------------------------------
  // 9. Styles
  // -----------------------------------------------------------------
  const customStyles = {
    control: (s: any) => ({
      ...s,
      minHeight: "30px",
      fontSize: "14px",
    }),
    multiValue: (s: any) => ({
      ...s,
      backgroundColor: "#007BFF",
      borderRadius: "4px",
      padding: "2px 4px",
    }),
    multiValueLabel: (s: any) => ({
      ...s,
      color: "#FFF",
      fontSize: "12px",
    }),
    multiValueRemove: (s: any) => ({
      ...s,
      color: "#FFF",
      ":hover": { backgroundColor: "#0056b3" },
    }),
    option: (s: any, { isSelected, isFocused }: any) => ({
      ...s,
      backgroundColor: isSelected ? "#007BFF" : isFocused ? "#E6F0FA" : "#FFF",
      color: isSelected ? "#FFF" : "#000",
      fontSize: "14px",
    }),
  };

  const Menu = (props: MenuProps<Option, true>) => (
    <div {...qaAttrs(qaIdPrefix, "menu")}>
      <components.Menu {...props} />
    </div>
  );

  const OptionComponent = (props: OptionProps<Option, true>) => (
    <div
      {...qaAttrs(qaIdPrefix, `option-${qaSafe(String(props.data.value))}`)}
      data-option-label={props.data.label}
      data-option-value={props.data.value}
    >
      <components.Option {...props} />
    </div>
  );

  const MultiValueComponent = (props: MultiValueProps<Option, true>) => (
    <div
      {...qaAttrs(qaIdPrefix, `selected-${qaSafe(String(props.data.value))}`)}
      data-selected-label={props.data.label}
      data-selected-value={props.data.value}
    >
      <components.MultiValue {...props} />
    </div>
  );

  return (
    <div {...qaAttrs(qaIdPrefix, "wrapper")}>
      <Select
        isMulti
        isClearable
        closeMenuOnSelect={false}
        inputId={inputQaId}
        instanceId={selectQaId}
        name={selectQaId}
        classNamePrefix={selectQaId}
        className={selectQaId}
        aria-label="Patient selector"
        data-testid={selectQaId}
        data-qa={selectQaId}
        value={selectedOptions}
        onChange={handleChange}
        onInputChange={onInputChange}
        inputValue={inputValue}
        options={options}
        isLoading={loading}
        placeholder="Select patient..."
        styles={customStyles}
        noOptionsMessage={noOptionsMessage}
        components={{
          Menu,
          Option: OptionComponent,
          MultiValue: MultiValueComponent,
        }}
      />

      <span
        {...qaAttrs(qaIdPrefix, "loading-state")}
        data-loading={loading ? "true" : "false"}
        style={{ display: "none" }}
      >
        {loading ? "Loading" : "Idle"}
      </span>

      <span
        {...qaAttrs(qaIdPrefix, "selected-count")}
        data-count={selectedOptions.length}
        style={{ display: "none" }}
      >
        {selectedOptions.length}
      </span>

      <span
        {...qaAttrs(qaIdPrefix, "options-count")}
        data-count={options.length}
        style={{ display: "none" }}
      >
        {options.length}
      </span>

      <span
        {...qaAttrs(qaIdPrefix, "input-value")}
        data-value={inputValue}
        style={{ display: "none" }}
      >
        {inputValue}
      </span>
    </div>
  );
};

export default VipPatientSelector;
